import React, { useEffect, useState } from "react";
import Papa from "papaparse"; 
import CommonTable from "../CommonTable/CommonTable";
import { Change, manResponseType } from "../../interfaces";
import {
  associateValueToRule,
  getProcessedChanges,
} from "../../Services/ChangeService";
import { ChangeStateEnum } from "../../Enum";

type Props = {};

const AllChanges = (props: Props) => {
  const [historicalChanges, setHistoricalChanges] = useState<Change[]>([]);
  const [manifestNames, setManifestNames] = useState<Record<string, string>>(
    {}
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchPatientId, setSearchPatientId] = useState<string>(""); // Estado para Patient ID
  const [searchManifestation, setSearchManifestation] = useState<string>(""); // Estado para Manifestation
  const [visibleChanges, setVisibleChanges] = useState<Change[]>([]);
  const [responseDescriptions, setResponseDescriptions] = useState<
    manResponseType[]
  >([]);

  const [idManResponseAvailable, setIdManResponseAvailable] = useState<
    number[]
  >([]);

  const [filteredChanges, setFilteredChanges] = useState<Change[]>([]);

  useEffect(() => {
    const filterChanges = () => {
      const filtered = historicalChanges.filter((change) => {
        // Obtener el nombre de la manifestación
        const manifestation =
          manifestNames[change.variableName] || change.variableName;

        // Aplicar los filtros
        return (
          change.identification
            .toLowerCase()
            .includes(searchPatientId.toLowerCase()) &&
          manifestation
            .toLowerCase()
            .includes(searchManifestation.toLowerCase())
        );
      });

      setFilteredChanges(filtered);
    };

    filterChanges();
  }, [searchPatientId, searchManifestation, historicalChanges, manifestNames]);

  // Cargar nombres del archivo CSV
  useEffect(() => {
    const fetchManifestNames = async () => {
      try {
        const response = await fetch("/nombre_manifestaciones.csv");
        const csvText = await response.text();

        // Parsear el CSV
        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (result: any) => {
            const names: Record<string, string> = {};
            result.data.forEach((row: string[]) => {
              if (row[0] && row[1]) {
                names[row[0]] = row[1];
              }
            });
            setManifestNames(names);
          },
        });

        // mostrar en consola los nombres de las manifestaciones
        console.log("Nombres de manifestaciones cargados:", manifestNames);
      } catch (error) {
        console.error("Error al cargar los nombres de manifestaciones:", error);
      }
    };

    fetchManifestNames();
  }, []);

  useEffect(() => {
    const fetchChanges = async () => {
      try {
        console.log("Fetching validation rules...");
        const processedChanges = await getProcessedChanges();
        console.log("Validation rules fetched:", processedChanges);
        setHistoricalChanges(processedChanges);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching rules:", error);
        setError("Failed to fetch rules. Please try again later.");
        setLoading(false);
      }
    };

    fetchChanges();
  }, []);

  // Cargar ID MAN RESPONSES del archivo CSV
  useEffect(() => {
    const fetchResponseDescription = async () => {
      try {
        const response = await fetch("/response_description.csv");
        const csvText = await response.text();

        // Parsear el CSV
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (result: any) => {
            const parsedData: manResponseType[] = [];

            result.data.forEach((row: any) => {
              // Ignorar filas incompletas o erróneas
              if (
                row.Id &&
                row.Description &&
                row.ResponseType &&
                row.id &&
                row.description_es &&
                row.description_es !== "#ERROR!"
              ) {
                parsedData.push({
                  Id: Number(row.Id),
                  Description: row.Description,
                  ResponseType: row.ResponseType,
                  id: Number(row.id),
                  description_es: row.description_es,
                });
              }
            });
            setResponseDescriptions(parsedData);
            const uniqueIds = Array.from(
              new Set(parsedData.map((item) => item.Id))
            );
            setIdManResponseAvailable(uniqueIds);
          },
        });
      } catch (error) {
        console.error("Error al cargar los nombres de manifestaciones:", error);
      }
    };

    fetchResponseDescription();
  }, []);

  const [resolvedOldValues, setResolvedOldValues] = useState<
    Record<string, string>
  >({});
  const [resolvedNewValues, setResolvedNewValues] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const fetchResolvedValues = async () => {
      if (
        visibleChanges.length === 0 ||
        responseDescriptions.length === 0 ||
        idManResponseAvailable.length === 0
      )
        return;

      const oldMap: Record<string, string> = {};
      const newMap: Record<string, string> = {};

      const changesToResolve = visibleChanges.filter(
        (change) => !(change.id in resolvedOldValues)
      );

      if (changesToResolve.length === 0) return;

      await Promise.all(
        changesToResolve.map(async (change) => {
          try {
            if (change.variableName.endsWith("1")) {
              var index = change.oldValue.indexOf("T");
              if (index !== -1) {
                oldMap[change.id] = change.oldValue.substring(0, index);
              } else {
                oldMap[change.id] = change.oldValue;
              }
              var index = change.newValue.indexOf("T");
              if (index !== -1) {
                newMap[change.id] = change.newValue.substring(0, index);
              } else {
                newMap[change.id] = change.newValue;
              }
            } else {
              const oldValue = await associateValueToRule(
                change.oldValue,
                change.variableName,
                [change.id_Phase],
                idManResponseAvailable,
                responseDescriptions
              );
              var index = oldValue[0].indexOf("T");

              if (change.oldValue == oldValue[0]) {
                if (index !== -1) {
                  oldValue[0] = oldValue[0].substring(0, index);
                }
                oldMap[change.id] = oldValue[0];
              } else {
                if (index !== -1) {
                  oldValue[0] = oldValue[0].substring(0, index);
                }
                oldMap[change.id] = `${oldValue[0]} (${change.oldValue})`;
              }

              const newValue = await associateValueToRule(
                change.newValue,
                change.variableName,
                [change.id_Phase],
                idManResponseAvailable,
                responseDescriptions
              );

              var index = newValue[0].indexOf("T");

              if (change.newValue == newValue[0]) {
                if (index !== -1) {
                  newValue[0] = newValue[0].substring(0, index);
                }
                newMap[change.id] = newValue[0];
              } else {
                if (index !== -1) {
                  newValue[0] = newValue[0].substring(0, index);
                }
                newMap[change.id] = `${newValue[0]} (${change.newValue})`;
              }
            }
          } catch {
            oldMap[change.id] = change.oldValue;
            newMap[change.id] = change.newValue;
          }
        })
      );
      setResolvedOldValues((prev) => ({ ...prev, ...oldMap }));
      setResolvedNewValues((prev) => ({ ...prev, ...newMap }));
    };

    fetchResolvedValues();
  }, [visibleChanges, responseDescriptions, idManResponseAvailable]);

  const changetableConfig = (manifestNames: Record<string, string>) => [
    {
      key: "id_Patient",
      label: "Patient ID",
      render: (manifest: Change) => (
        <a
          href={`https://arthrosoft.com/es/Project/Project/PatientClinicHistory/${manifest.id_Patient}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          {manifest.identification}
        </a>
      ),
      subTitle: "Identificador del paciente (clic para ver historia clínica)",
    },
    {
      label: "Manifestation",
      render: (manifest: Change) =>
        manifestNames[manifest.variableName] || manifest.variableName,
      subTitle: "The manifestation name",
    },
    {
      label: "Created By",
      render: (manifest: Change) => manifest.changedBy,
      subTitle: "The user who created the rule",
    },
    {
      label: "Previous Value",
      render: (manifest: Change) => {
        var oldValue = manifest.oldValue;
        var index = oldValue.indexOf("T");
        if (index !== -1) {
          oldValue = oldValue.substring(0, index);
        }
        return resolvedOldValues[manifest.id] || oldValue;
      },
      subTitle: "The previous value",
    },
    {
      label: "Current Value",
      render: (manifest: Change) => {
        var newValue = manifest.newValue;
        var index = newValue.indexOf("T");
        if (index !== -1) {
          newValue = newValue.substring(0, index);
        }
        return resolvedNewValues[manifest.id] || newValue;
      },
      subTitle: "The previous value",
    },
    {
      label: "Comment",
      render: (manifest: Change) => manifest.comment,
      subTitle: "The new value",
    },
    {
      label: "State",
      render: (manifest: Change) => {
        return ChangeStateEnum[manifest.changeState];
      },
      subTitle: "The state of the rule",
    },
  ];

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <input
          type="text"
          placeholder="Buscar por Patient ID (e.g., C10P14)"
          value={searchPatientId}
          onChange={(e) => setSearchPatientId(e.target.value)}
          className="border border-gray-300 rounded p-2"
        />
        <input
          type="text"
          placeholder="Buscar por Manifestation"
          value={searchManifestation}
          onChange={(e) => setSearchManifestation(e.target.value)}
          className="border border-gray-300 rounded p-2"
        />
      </div>
      <CommonTable
        data={filteredChanges}
        configs={changetableConfig(manifestNames)}
        onVisibleRowsChange={setVisibleChanges}
      />
    </>
  );
};

export default AllChanges;
